/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';

import * as S from './style';
import ManageLocationForm from './ManageLocationForm';
import { postCRUD, putCRUD } from '../../../services/crudService';
import { useGeneralContext } from '../../../context/GeneralContext';
import { useLocationsCacheContext } from '../../../context/LocationsCacheContext';
import { queryClient } from '../../../services/queryClient';
import { useConfirmation } from '../../../context/ConfirmationContext';
import { useAssetFormContext } from '../../../context/AssetFormContext';
import Yup from '../../../services/yupLocale';

const LocationSchema = Yup.object().shape({
  name: Yup.string().required().max(50),
  description: Yup.string().max(255),
  user_group: Yup.array().of(Yup.string()).min(1).required(),
  lng: Yup.number().required(),
  lat: Yup.number().required(),
  coordinates: Yup.array().of(Yup.number()).required(),
  buildings: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().required().max(50),
        description: Yup.string().max(255),
        lng: Yup.number().required(),
        lat: Yup.number().required(),
      }),
    )
    .required()
    .test('unique-building-names', '', (buildings, ctx) => {
      if (!Array.isArray(buildings)) return true;

      const norm = s =>
        String(s ?? '')
          .trim()
          .toLowerCase();
      const map = new Map();

      buildings.forEach((b, i) => {
        const k = norm(b?.name);
        if (!k) return; // empty names handled by nested "required"
        const arr = map.get(k) ?? [];
        arr.push(i);
        map.set(k, arr);
      });

      // Build errors for all duplicates EXCEPT the first index
      const errors = Array.from(map.values()).flatMap(idxs =>
        idxs.length > 1
          ? idxs.slice(1).map(i =>
              ctx.createError({
                path: `${ctx.path}[${i}].name`,
                message: 'tools_menu.location.unique_building_name',
              }),
            )
          : [],
      );

      return errors.length ? new Yup.ValidationError(errors) : true;
    }),
});

const LocationManagement = ({ editing, clearEditing, popPage }) => {
  const { t } = useTranslation();
  const { setIsLocationClone } = useAssetFormContext();
  const initialValues = {
    id: editing?.id || null,
    name: editing?.name || '',
    description: editing?.description || '',
    user_group: editing?.user_group || [],
    lat: editing?.lat,
    lng: editing?.lng,
    coordinates: editing?.coordinates || [],
    buildings: editing?.buildings || [],
    deleted: [],
    assets: editing?.assets || [],
  };
  const { openToast } = useGeneralContext();
  const { refreshMap } = useLocationsCacheContext();
  const { openConfirmation } = useConfirmation();

  const handleSubmit = async data => {
    try {
      const finalData = {
        ...data,
        id: editing?.id,
        buildings: data.buildings.map(b => {
          const existingBuilding = editing?.buildings.find(
            bld => bld.id === b.id,
          );
          return {
            id: existingBuilding?.id || b.clone_id,
            name: b.name,
            description: b.description,
            lng: b.lng,
            lat: b.lat,
            mode: b.mode,
          };
        }),
        assets: data.assets.map(a => ({
          id: a.id,
          is_placed: a.is_placed,
          lng: a.lng,
          lat: a.lat,
        })),
      };
      if (editing?.id) {
        await putCRUD({ entity: 'location', data: finalData, id: editing.id });
      } else {
        await postCRUD({ entity: 'location', data: finalData });
      }
      queryClient.removeQueries({ queryKey: 'locationsCache', exact: true });
      setTimeout(() => {
        refreshMap();
        openToast(t('tools_menu.location.save_success'), 'success');
        popPage();
        setIsLocationClone(false);
      }, 100);
    } catch (err) {
      console.error(err);
      if (err.response.status === 409) {
        openConfirmation({
          title: t('tools_menu.location.save_conflict_title'),
          message: t('tools_menu.location.save_conflict_message', {
            name: data.name,
          }),
          actionLabel: t('modal.ok'),
          preventClickOutside: true,
          hideCancel: true,
        });
      } else {
        openToast(t('tools_menu.location.save_error'), 'error');
      }
    }
  };

  return (
    <S.LocationManagement>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnBlur
        validateOnChange
        validationSchema={LocationSchema}
      >
        {props => (
          <ManageLocationForm
            {...props}
            popPage={popPage}
            clearEditing={clearEditing}
          />
        )}
      </Formik>
    </S.LocationManagement>
  );
};

LocationManagement.defaultProps = {
  clearEditing: () => null,
  editing: null,
};

LocationManagement.propTypes = {
  clearEditing: PropTypes.func,
  editing: PropTypes.object,
  popPage: PropTypes.func.isRequired,
};

export default LocationManagement;
